import { createApp } from "vue";
import App from "./App.vue";
import router from "./router/index.js";
import vuetify from "./plugins/vuetify";
import { loadFonts } from "./plugins/webfontloader";
import { createPinia } from "pinia";
import "@google/model-viewer";
import { useAuthStore } from "./stores/authStore"; // ← 追加
import { getAuth, onAuthStateChanged, signInWithCustomToken, signOut } from "firebase/auth";
import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";
import i18n from "./i18n/index.js";
import * as Sentry from "@sentry/vue";

const pinia = createPinia(); // ✅ Piniaを作成

// loadFonts()

// ── デプロイ後の chunk 取得失敗を自動復旧 ──
// Vite はビルド毎に chunk のハッシュが変わるため、新デプロイ後に古いタブが
// 旧 URL の動的 import / CSS preload を取得しようとして失敗する
// (Sentry: "Failed to fetch dynamically imported module" / "Unable to preload CSS")。
// 一度だけ自動リロードして最新アセットを取得する。直近に既にリロード済みなら
// 「本当にアセットが存在しない」ケースとみなし、無限リロードを避ける。
window.addEventListener("vite:preloadError", () => {
  const KEY = "vite:preloadError:lastReload";
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (Date.now() - last < 10_000) return; // 直近10秒以内に再発ならループ防止で何もしない
  sessionStorage.setItem(KEY, String(Date.now()));
  window.location.reload();
});

const app = createApp(App);

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? "production",
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: true,
      }),
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [[500, 599]],
        failedRequestTargets: [/poranos\.com/],
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    beforeSend(event) {
      if (event.message?.includes("postMessage sign-in failed")) return null;
      return event;
    },
  });
}

app.use(pinia);
app.use(router);
app.use(vuetify);
app.use(i18n);
app.use(Toast, {
  position: "top-right",
  timeout: 8000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: false,
  closeButton: "button",
  icon: true,
});

const authStore = useAuthStore();

authStore.initAuth().then(() => {
  // authStore.handleRedirectResult();
  app.use(router);
  app.mount("#app");
});

// ── Poranos Desktop iframe連携: postMessageでログイン/ログアウト ──
if (window !== window.parent) {
  const API_BASE = import.meta.env.VITE_API_BASE_URL || "";

  const ALLOWED_ORIGINS = [
    window.location.origin,
    "https://poranos.com",
    "https://www.poranos.com",
    "http://localhost:8080",  // Poranos Desktop (Electron)
    "http://127.0.0.1:8080", // Poranos Desktop (Electron) — 127.0.0.1 variant
  ];

  window.addEventListener("message", async (event) => {
    if (!event.data?.type) return;

    if (!ALLOWED_ORIGINS.includes(event.origin)) {
      console.warn("[iframe] Blocked postMessage from unauthorized origin:", event.origin);
      return;
    }

    if (event.data.type === "poranos-auth-token" && event.data.token) {
      try {
        // IDトークンをバックエンドに送信してCustom Tokenを取得
        const res = await fetch(`${API_BASE}/create-custom-token/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_token: event.data.token }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const { custom_token } = await res.json();

        // Custom TokenでFirebase認証
        const auth = getAuth();
        await signInWithCustomToken(auth, custom_token);
        console.log("[iframe] Signed in via postMessage");

        // 親ウィンドウに成功を通知
        event.source?.postMessage({ type: "poranos-auth-result", success: true }, { targetOrigin: event.origin });
      } catch (err) {
        console.error("[iframe] postMessage sign-in failed:", err);
        event.source?.postMessage({ type: "poranos-auth-result", success: false, error: String(err) }, { targetOrigin: event.origin });
      }
    }

    if (event.data.type === "poranos-auth-logout") {
      try {
        const auth = getAuth();
        await signOut(auth);
        console.log("[iframe] Signed out via postMessage");
      } catch (err) {
        console.error("[iframe] postMessage sign-out failed:", err);
      }
    }
  });
}
